/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { CSSProperties, FC, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'

import { getJSON } from '../../../common/services/api'
import { gaEvent } from '../../../common/services/analytics'
import { colors } from '@lmc/common/constants'
import { getNominative } from '../../../common/utils'
import { ResponsiveText } from '../../../LEGACY/elemental'
import {
  Spinner,
  ProgressRing,
  DashboardListItem,
} from '../../../components/generic'
import { useSelector } from 'react-redux'
import { RouterProps } from '@lmc/common/types'
import { CardTitle, EmptyItems } from './CardComponents'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { getProgressColor } from './utils'

type Props = {
  isLoadingProp: boolean
  router?: RouterProps
  homeID?: string
  hasPro?: boolean
}

export const CarePlansCard: FC<Props> = ({
  router,
  isLoadingProp,
  homeID,
  hasPro,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [percentage, setPercentage] = useState<number>()

  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const [carePlanCounts, setCarePlanCounts] = useState<{
    thisWeek: string
    overdue: string
    today: string
  }>()

  const hasMounted = useRef(false)

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const fetchPercentage = async () => {
    try {
      const percentage = await getJSON({
        url: '/api/list/care-plans?percentageComplete=true',
      })
      const utcOffset = dayjs().utcOffset()
      const carePlanCounts = await getJSON({
        url: `/api/aggregations/care-plans?utcOffset=${utcOffset}`,
      })

      setPercentage(percentage.result)
      if (carePlanCounts.result) {
        setCarePlanCounts({
          thisWeek: carePlanCounts.result.week,
          overdue: carePlanCounts.result.overdue,
          today: carePlanCounts.result.today,
        })
      }
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPercentage()
  }, [])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    fetchPercentage()
  }, [homeID])

  const redirect = () => {
    const url = carePlanCounts
      ? '/reports/care-planning/care-plans'
      : '/residents?carePlans=true'
    router?.push(url)
  }

  const triggerGAEvent = (action: string) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label: undefined,
    })

  const renderTitle = () => (
    <CardTitle
      title={getNominative(carePlanNominative, 'Care Plans')}
      onClick={() => {
        triggerGAEvent('Click care_plans_title_link')
        redirect()
      }}
    />
  )

  const preferredCarePlanNameButton = getNominative(
    carePlanNominative,
    'care plan'
  )?.toLocaleLowerCase()

  const renderEmptyMessage = () => (
    <EmptyStateWrapper>
      <EmptyItems items={3} />
      {hasPro && (
        <InfoWrapper>
          <StyledText>
            {t(selfServeConstants.SEE_WHAT, { variable: carePlanNominative })}
          </StyledText>
          <div onClick={redirect}>
            <StyledTextHere>
              {t(selfServeConstants.VIEW_EXAMPLE, {
                variable: preferredCarePlanNameButton,
              })}
            </StyledTextHere>
          </div>
          <HereWrapper />
        </InfoWrapper>
      )}
    </EmptyStateWrapper>
  )

  const renderBody = () => {
    if (!carePlanCounts || !percentage) {
      return renderEmptyMessage()
    }

    const { today, thisWeek, overdue } = carePlanCounts
    return (
      <div onClick={redirect} style={styles.notifications}>
        <DashboardListItem
          iconName="overdue"
          iconFill={colors.red}
          text="due today"
          count={today}
        />
        <DashboardListItem
          iconName="clock"
          iconFill={colors.orange}
          text="due this week"
          count={thisWeek}
        />
        <DashboardListItem
          iconName="overdue"
          iconFill={colors.red}
          text="overdue"
          count={overdue}
        />
      </div>
    )
  }

  const renderFooter = () => {
    if (percentage === 0) return null

    return (
      <div className="lmc-home-card-footer" style={styles.footer}>
        <div style={styles.footerContainer}>
          <div style={styles.progressContainer}>
            <ProgressRing
              color={getProgressColor(percentage)}
              radius={45}
              progress={percentage}
              style={styles.progressRing}
            />
            <ResponsiveTextStyled
              visibleSM="completed"
              visibleMD="of all completed"
              visibleLG={`of all ${getNominative(
                carePlanNominative,
                'Care Plans'
              )} completed`}
            />
          </div>
        </div>
      </div>
    )
  }

  if (!router) return null

  const isLoadingAll = isLoading || isLoadingProp

  return (
    <div>
      <div
        className="lmc-home-card"
        style={styles.mainContainer as CSSProperties}
      >
        {renderTitle()}
        <div className="lmc-home-card-body-ss" style={styles.body}>
          {isLoadingAll ? <Spinner style={styles.spinner} /> : renderBody()}
        </div>
        {!isLoadingAll && renderFooter()}
      </div>
    </div>
  )
}

const styles = {
  body: {
    minHeight: 0,
    marginTop: 5,
  },
  buttonText: {
    color: colors.black,
    opacity: 0.6,
  },
  emptyHeader: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  emptyLink: {
    margin: '0px 5px',
  },
  emptyMessage: {
    paddingTop: 20,
  },
  footerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  link: {
    cursor: 'pointer',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  notifications: {
    cursor: 'pointer',
  },
  progressContainer: {
    alignItems: 'center',
    color: colors.darkgrey,
    display: 'flex',
  },
  progressRing: {
    marginRight: 8,
  },
  spinner: {
    paddingTop: 30,
  },
  title: {
    display: 'inline-block',
    textDecoration: 'none',
  },
  footer: { transform: 'translateY(3px)', marginTop: 'auto' },
}

const InfoWrapper = styled.div`
  background-color: #fff8fa;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const HereWrapper = styled.div`
  display: flex;
`

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: auto;
`

const StyledTextHere = styled(Text)`
  color: ${({ theme }) => theme.color.status.popout};
  text-decoration: underline;
  cursor: pointer;
`

const ResponsiveTextStyled = styled(ResponsiveText)`
  color: ${(props) => props.theme.color.neutral.color4};
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 12px;
`

export default CarePlansCard
